import classnames from 'classnames/bind'
import { useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalBreakpoints } from '~/@types/breakpoints'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { Spinner } from '@unlikelystudio/react-abstract-components'

import { ArrowBottomIcon } from '~/components/Abstracts/Icons'
import List from '~/components/Navigation/BlogNavigation/List'
import PanelList from '~/components/Navigation/BlogNavigation/PanelList'

import { useStyle } from '~/providers/StyleProvider'

import useBreakpoint from '~/hooks/useBreakpoint'
import useTranslatedSmartCount from '~/hooks/useTranslatedSmartCount'

import { BLOG, FILTER } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export const ALL_NAME = 'all'

export const getAllChecked = (items: Category[]) => {
  const checkedItemsLength =
    items?.filter(({ checked }) => checked)?.length ?? null

  return checkedItemsLength === 0
}

export interface Category {
  id: string
  key: string
  value: string
  checked?: boolean
}

export interface BlogNavigationProps {
  className?: string
  title?: string
  categories?: Category[]
  count?: number
  isLoading?: boolean
}

function BlogNavigation({
  className,
  title,
  categories,
  count,
  isLoading,
}: BlogNavigationProps) {
  const t = useTranslate()

  const isMobile = useBreakpoint(GlobalBreakpoints.LG)

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const itemStyle = useStyle({
    textPreset: GlobalTextPreset.Cta12Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const countStyle = useStyle({
    textPreset: GlobalTextPreset.Cta12Haffer,
    color: GlobalThemeColors.DoveGray,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const COUNT_TRANSLATION = useTranslatedSmartCount(
    [BLOG.FILTER_AVAILABILITY, BLOG.FILTER_AVAILABILITIES],
    count,
  )

  return (
    <>
      <div className={cx(css.BlogNavigation, className)}>
        <div className={cx(css.top, gridStyle)}>
          <div className={css.content}>
            <div className={cx(css.left)}>
              {isMobile ? (
                <>
                  <button
                    className={cx(css.item, itemStyle, {
                      isActive: isPanelOpen,
                    })}
                    onClick={() => setIsPanelOpen((p) => !p)}
                    aria-label={t(FILTER.CATEGORIES)}>
                    {t(FILTER.CATEGORIES)}
                    <ArrowBottomIcon
                      className={cx(css.arrowBottom, {
                        isActive: isPanelOpen,
                      })}
                    />
                  </button>
                </>
              ) : (
                <List title={title} items={categories} />
              )}
            </div>
            <div className={cx(css.right, countStyle)}>
              {isLoading && <Spinner className={cx(css.loading)} />}
              {count <= 0 ? t(FILTER.NO_RESULT) : COUNT_TRANSLATION}
            </div>
          </div>
        </div>
      </div>

      <PanelList
        isOpen={isMobile && isPanelOpen}
        title={title}
        items={categories}
        onClose={() => setIsPanelOpen(false)}
      />
    </>
  )
}

BlogNavigation.defaultProps = {}

export default BlogNavigation
